import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { NAVIGATION_ITEMS } from './nav-items';
import { StatusBadge } from '@/components/common/StatusBadge';

interface SidebarNavProps {
  orgName?: string | null;
  orgStatus: string;
  hasPermission: (permission: string) => boolean;
}

export const SidebarNav: React.FC<SidebarNavProps> = ({
  orgName,
  orgStatus,
  hasPermission,
}) => {
  const location = useLocation();

  return (
    <aside className="hidden md:flex w-64 flex-col bg-[#0A0B0E] border-r border-zinc-800/70 p-4 shrink-0">
      <div className="mb-4 px-2 py-2.5 bg-zinc-900/60 border border-zinc-800/60 rounded-xl">
        <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">
          Active Organization
        </p>
        <p className="text-sm font-semibold text-zinc-100 truncate mt-0.5">
          {orgName || 'Workspace'}
        </p>
        <div className="mt-2 flex items-center justify-between pt-2 border-t border-zinc-800/50">
          <span className="text-[10px] text-zinc-400">Status</span>
          <StatusBadge status={orgStatus} size="xs" />
        </div>
      </div>

      <nav className="space-y-1 flex-1">
        {NAVIGATION_ITEMS.map((item) => {
          const Icon = item.icon;
          const isAllowed = !item.permission || hasPermission(item.permission);
          const isActive = location.pathname === item.href;

          return (
            <NavLink
              key={item.href}
              to={item.href}
              className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? 'bg-indigo-600/15 text-indigo-300 border border-indigo-500/30'
                  : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900/60'
              } ${!isAllowed ? 'opacity-40 pointer-events-none' : ''}`}
            >
              <div className="flex items-center gap-3">
                <Icon className={`w-4 h-4 ${isActive ? 'text-indigo-400' : 'text-zinc-500'}`} />
                <span>{item.name}</span>
              </div>
              {!isAllowed && (
                <span className="text-[10px] uppercase font-mono px-1 py-0.5 bg-zinc-800 text-zinc-400 rounded">
                  Locked
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="pt-4 border-t border-zinc-800/60 mt-auto">
        <div className="flex items-center justify-between px-2 text-[11px] text-zinc-400">
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            Partner Portal
          </span>
          <a
            href="https://conversora.io"
            target="_blank"
            rel="noreferrer"
            className="hover:text-zinc-300 flex items-center gap-1 text-[11px]"
          >
            conversora.io
            <ExternalLink className="w-2.5 h-2.5" />
          </a>
        </div>
      </div>
    </aside>
  );
};
